// frontend/src/components/inventaire/HistoriqueFideliteModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  IconButton,
  Alert,
  Card,
  CardContent,
  Grid,
  Divider,
  LinearProgress
} from '@mui/material';
import {
  Loyalty as LoyaltyIcon,
  Close as CloseIcon,
  AddCircle as AddIcon,
  RemoveCircle as RemoveIcon,
  Star as StarIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import api from '../../services/auth';
import { formatCurrency, formatDate, formatPhoneNumber } from '../../../src/components/utils/formatters';

const HistoriqueFideliteModal = ({ open, onClose, client, embedded = false }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (open && client?.id) {
      fetchHistorique();
    }
  }, [open, client]);

  const fetchHistorique = async () => {
    setLoading(true);
    try {
      const response = await api.get(`inventory/clients/${client.id}/historique_fidelite/`);
      setData(response.data);
      setError(null);
    } catch (err) {
      console.error('Erreur chargement historique fidélité:', err);
      setError('Impossible de charger l\'historique des points fidélité');
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'gain':
        return <AddIcon fontSize="small" color="success" />;
      case 'utilisation':
        return <RemoveIcon fontSize="small" color="error" />;
      case 'expiration':
        return <HistoryIcon fontSize="small" color="warning" />;
      default:
        return <LoyaltyIcon fontSize="small" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'gain': return 'success';
      case 'utilisation': return 'error';
      case 'expiration': return 'warning';
      default: return 'default';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'gain': return 'Gain';
      case 'utilisation': return 'Utilisation';
      case 'expiration': return 'Expiration';
      default: return type;
    }
  };

  const calculateNiveau = (points) => {
    if (points >= 1000) return { niveau: 'Or', color: '#FFD700', progress: 100 };
    if (points >= 500) return { niveau: 'Argent', color: '#C0C0C0', progress: 75 };
    if (points >= 100) return { niveau: 'Bronze', color: '#CD7F32', progress: 50 };
    return { niveau: 'Standard', color: '#2196f3', progress: 25 };
  };

  const niveauInfo = calculateNiveau(data?.points_actuels || 0);
  const pointsVersProchainNiveau = 
    data?.points_actuels < 100 ? 100 - data?.points_actuels :
    data?.points_actuels < 500 ? 500 - data?.points_actuels :
    data?.points_actuels < 1000 ? 1000 - data?.points_actuels : 0;

  const content = (
    <Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      ) : data ? (
        <Grid container spacing={3}>
          {/* Carte des points actuels */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StarIcon sx={{ color: niveauInfo.color }} />
                    <Typography variant="h6">
                      Points de fidélité
                    </Typography>
                  </Box>
                  <Chip 
                    label={`Niveau ${niveauInfo.niveau}`}
                    sx={{ 
                      bgcolor: niveauInfo.color,
                      color: niveauInfo.niveau === 'Or' ? 'black' : 'white',
                      fontWeight: 'bold'
                    }}
                  />
                </Box>
                
                <Typography variant="h3" sx={{ mb: 2, color: niveauInfo.color }}>
                  {data.points_actuels}
                </Typography>
                
                {pointsVersProchainNiveau > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="textSecondary">
                        Progression vers prochain niveau
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {pointsVersProchainNiveau} points restants
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={niveauInfo.progress} 
                      sx={{ 
                        height: 10, 
                        borderRadius: 5,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: niveauInfo.color
                        }
                      }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Historique des transactions */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon />
              Historique des transactions
            </Typography>
            
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Points</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Source</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.transactions?.map((transaction) => (
                    <TableRow key={transaction.id} hover>
                      <TableCell>{formatDate(transaction.date_creation)}</TableCell>
                      <TableCell>
                        <Chip
                          icon={getTypeIcon(transaction.type)}
                          label={getTypeLabel(transaction.type)}
                          size="small"
                          color={getTypeColor(transaction.type)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography 
                          color={transaction.points > 0 ? 'success.main' : 'error.main'}
                          fontWeight="bold"
                        >
                          {transaction.points > 0 ? '+' : ''}{transaction.points}
                        </Typography>
                      </TableCell>
                      <TableCell>{transaction.description || '-'}</TableCell>
                      <TableCell>{transaction.source || '-'}</TableCell>
                    </TableRow>
                  ))}
                  {(!data.transactions || data.transactions.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        Aucune transaction de fidélité
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      ) : null}
    </Box>
  );

  if (embedded) {
    return content;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LoyaltyIcon color="warning" />
          <Typography variant="h6">
            Programme fidélité - {client?.nom_complet || `${client?.nom} ${client?.prenom || ''}`}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {content}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
};

export default HistoriqueFideliteModal;